import { useCallback, useEffect, useMemo, useState } from 'react';
import { useHref, useNavigate, useParams } from 'react-router-dom';
import { useCriteriaParams } from '../../../app/use-criteria-params';
import { useQuery } from '@tanstack/react-query';
import { useTranslate } from '@tolgee/react';
import {
  MudButton,
  MudChip,
  MudDateInput,
  MudIcon,
  MudSelect,
  MudTable,
} from '@egov-moldova/mud-react';
import {
  dateInputToIso,
  EMPTY_CELL,
  EmptyRegisterMessage,
  FilterBar,
  formatDateTime,
  isoToDateInput,
  onDateCommit,
  RouterLink,
  type ActiveFilter,
  onRowOpen,
} from '@simsm/ui-shared';
import { listSubmissionFacets, listSubmissions } from '../../../features/reports/api';
import { registrarFacetLabel } from '../../../components/StockChartTable/registrar-label';
import { useSession } from '../../../hooks/use-session';
import { QueryState } from '../../../components/QueryState/QueryState';
import {
  correlateFilters,
  DEFAULT_PERIOD,
  PERIOD_IDS,
  resolvePeriod,
  todayIso,
  type FilterOption,
  type PeriodId,
} from './register-filters';
import { TableFooter, usePageSize } from '../../../components/TableFooter/TableFooter';

interface TableRowClickDetail {
  row: { id?: string | number };
  index: number;
}

const NONE = '';

export function SubmissionRegisterPage(): JSX.Element {
  const { t } = useTranslate();
  const navigate = useNavigate();
  const session = useSession();
  const [searchParams, setSearchParams] = useCriteriaParams();
  // The register lives under /system-admin and under /operator, so its links
  // stay in the section the user is actually browsing.
  const { role: roleSegment } = useParams<{ role: string }>();
  const rolePrefix = `/${roleSegment ?? 'operator'}`;
  const newReportPath = `${rolePrefix}/reporting/new`;
  const newReportHref = useHref(newReportPath);

  const activeRole = session.data?.activeRole ?? '';
  const isSystemAdministrator = activeRole === 'SYSTEM_ADMIN';
  const canFileReports = activeRole === 'REGISTRAR_ADMIN' || activeRole === 'REGISTRAR_OPERATOR';

  const today = todayIso();
  const periodParam = searchParams.get('per') ?? DEFAULT_PERIOD;
  const period = resolvePeriod(
    PERIOD_IDS.includes(periodParam as PeriodId) ? periodParam : DEFAULT_PERIOD,
    searchParams.get('from') ?? NONE,
    searchParams.get('to') ?? NONE,
    today,
  );

  const facetsQuery = useQuery({
    queryKey: ['submission-facets'],
    queryFn: listSubmissionFacets,
    staleTime: 5 * 60_000,
  });

  const filters = useMemo(
    () =>
      correlateFilters(facetsQuery.data ?? [], {
        registrarIdno: searchParams.get('registrar') ?? NONE,
        regionId: searchParams.get('region') ?? NONE,
        localityId: searchParams.get('locality') ?? NONE,
        siteIds: (searchParams.get('sites') ?? NONE).split(',').filter(Boolean),
      }),
    [facetsQuery.data, searchParams],
  );
  const applied = filters.applied;
  // TC-UC93-05-01: a Registrar reads «denumire + IDNO» in the filter and on its chip.
  const registrarOptions = filters.registrars.map((option) => ({
    ...option,
    label: registrarFacetLabel(option.label, option.id),
  }));

  const page = Math.max(1, Number.parseInt(searchParams.get('p') ?? '1', 10) || 1);
  // caiet UC93-02 — the report identifier, the one column of the six with no
  // other way to narrow by it (the wireframe's own box, Rapoarte.tsx:84). It is
  // a criterion of the request, not a filter over the fetched page: the register
  // pages server-side, so a client-side box would have searched these 25 rows.
  const search = searchParams.get('q') ?? NONE;

  const [pageSize, setPageSize] = usePageSize('submission-register');

  const query = useQuery({
    queryKey: ['submission-register', page, pageSize, period, applied, search],
    queryFn: () =>
      listSubmissions({
        page,
        pageSize,
        ...(search ? { q: search } : {}),
        ...(period.fromIso ? { from: period.fromIso } : {}),
        ...(period.toIso ? { to: period.toIso } : {}),
        ...(applied.registrarIdno ? { registrar: applied.registrarIdno } : {}),
        ...(applied.regionId ? { region: applied.regionId } : {}),
        ...(applied.localityId ? { locality: applied.localityId } : {}),
        ...(applied.siteIds.length > 0 ? { sites: applied.siteIds } : {}),
      }),
    staleTime: 30_000,
  });

  const writeParams = useCallback(
    (mutate: (params: URLSearchParams) => void) => {
      const next = new URLSearchParams(searchParams);
      mutate(next);
      next.delete('p');
      setSearchParams(next);
    },
    [searchParams, setSearchParams],
  );

  const setParam = useCallback(
    (key: string, value: string) => {
      writeParams((params) => (value ? params.set(key, value) : params.delete(key)));
    },
    [writeParams],
  );

  const onPeriodChange = useCallback(
    (value: string) => {
      writeParams((params) => {
        if (value === DEFAULT_PERIOD) params.delete('per');
        else params.set('per', value);
        if (value !== 'custom') {
          params.delete('from');
          params.delete('to');
        }
      });
    },
    [writeParams],
  );

  // The typed text lives here, not in the URL, until the URL can say it back.
  // mud-date-input emits its `value` prop on change (mudChange fires on blur and
  // carries `this.value`), and the React wrapper writes the prop on every render,
  // so a render landing between typing and Tab used to reset the field to the
  // URL's still-empty value and the committed date was lost. A draft is only
  // honoured while the URL still holds what it held when the typing started
  // (`base`), so a later commit or an external reset takes over again.
  // The field speaks `DD/MM/YYYY` (AGE's default, and the order Moldova reads a
  // date in); the URL keeps ISO, because that is what the query sends and what a
  // shared link has to survive in. `dateInputToIso` is the conversion.
  const [drafts, setDrafts] = useState<
    Partial<Record<'from' | 'to', { text: string; base: string }>>
  >({});
  useEffect(() => {
    if (period.id !== 'custom') setDrafts({});
  }, [period.id]);

  const customDateValue = (key: 'from' | 'to'): string => {
    const url = searchParams.get(key) ?? NONE;
    const draft = drafts[key];
    return draft && draft.base === url ? draft.text : isoToDateInput(url);
  };

  // Commit as soon as the typed text is a complete valid date, so Tab (or any
  // render in between) can no longer lose it; a partial date only updates the
  // draft.
  const onCustomDateInput = useCallback(
    (key: 'from' | 'to') => (e: CustomEvent<{ value: string }>) => {
      const text = e.detail.value;
      const base = searchParams.get(key) ?? NONE;
      setDrafts((current) => ({ ...current, [key]: { text, base } }));
      const iso = dateInputToIso(text);
      if (iso) setParam(key, iso);
    },
    [searchParams, setParam],
  );

  // The blur / Enter / calendar commit. `onDateCommit` drops the picker's own
  // `mudChange`, which escapes the input's shadow root and carries no `isoValue`.
  const onCustomDateChange = useCallback(
    (key: 'from' | 'to') =>
      onDateCommit((d) => {
        setDrafts((current) => ({ ...current, [key]: undefined }));
        setParam(key, dateInputToIso(d.value));
      }),
    [setParam],
  );

  const onRegionChange = useCallback(
    (value: string) => {
      writeParams((params) => {
        if (value) params.set('region', value);
        else params.delete('region');
        params.delete('locality');
      });
    },
    [writeParams],
  );

  const toggleSite = useCallback(
    (siteId: string) => {
      const next = applied.siteIds.includes(siteId)
        ? applied.siteIds.filter((id) => id !== siteId)
        : [...applied.siteIds, siteId];
      setParam('sites', next.join(','));
    },
    [applied.siteIds, setParam],
  );

  const periodOptions = useMemo(
    () => PERIOD_IDS.map((id) => ({ value: id, label: t(`reports_register.period.${id}`) })),
    [t],
  );

  // UC93-07: an inactive site is offered with its reports, and says so.
  const siteLabel = useCallback(
    (site: FilterOption) =>
      site.inactive ? t('common.filter.inactive_site', { name: site.label }) : site.label,
    [t],
  );

  const optionsWithAny = useCallback(
    (options: { id: string; label: string }[], anyLabel: string) => [
      { value: NONE, label: anyLabel },
      ...options.map((option) => ({ value: option.id, label: option.label })),
    ],
    [],
  );

  // caiet UC93-02 — exactly the six columns it names, in its order
  // (TC-UC93-02-01). The wireframe's "Raportat de" and "Rânduri" are not among
  // them, and the owner chose the caiet's list on 2026-10-13; both stay on the
  // report's own page (docs/shape-uc-93-submission-register.md §Business day,
  // filter values and history, §4).
  const columns = useMemo(
    () => [
      { key: 'registrar', label: t('reports_register.column.registrar') },
      { key: 'region', label: t('reports_register.column.region') },
      { key: 'locality', label: t('reports_register.column.locality') },
      { key: 'site', label: t('reports_register.column.site') },
      { key: 'id', label: t('reports_register.column.id') },
      { key: 'confirmedAt', label: t('reports_register.column.confirmed') },
    ],
    [t],
  );

  const submissions = query.data?.submissions ?? [];
  const totalCount = query.data?.totalCount ?? 0;
  const isFiltered = searchParams.toString().replace(/(^|&)p=\d+/, '') !== '';

  // The default period is the register's own opening state, so it becomes a
  // criterion only once the operator moves off it (wireframe Rapoarte.tsx:66).
  // Sites are many-valued: one chip each, as the wireframe draws them.
  const activeFilters: ActiveFilter[] = [
    ...(period.id !== DEFAULT_PERIOD
      ? [
          {
            key: 'period',
            label: t('reports_register.filter.period'),
            value:
              period.id === 'custom'
                ? period.fromIso && period.toIso
                  ? t('common.filter.range_value', { from: period.fromIso, to: period.toIso })
                  : t('reports_register.period.custom')
                : t(`reports_register.period.${period.id}`),
            onRemove: () =>
              writeParams((params) => {
                params.delete('per');
                params.delete('from');
                params.delete('to');
              }),
          },
        ]
      : []),
    ...(applied.registrarIdno
      ? [
          {
            key: 'registrar',
            label: t('reports_register.filter.registrar'),
            value:
              registrarOptions.find((o) => o.id === applied.registrarIdno)?.label ??
              applied.registrarIdno,
            onRemove: () => setParam('registrar', ''),
          },
        ]
      : []),
    ...(applied.regionId
      ? [
          {
            key: 'region',
            label: t('reports_register.filter.region'),
            value:
              filters.regions.find((o) => o.id === applied.regionId)?.label ?? applied.regionId,
            onRemove: () => onRegionChange(''),
          },
        ]
      : []),
    ...(applied.localityId
      ? [
          {
            key: 'locality',
            label: t('reports_register.filter.locality'),
            value:
              filters.localities.find((o) => o.id === applied.localityId)?.label ??
              applied.localityId,
            onRemove: () => setParam('locality', ''),
          },
        ]
      : []),
    ...applied.siteIds.map((siteId) => {
      const site = filters.sites.find((o) => o.id === siteId);
      return {
        key: `site-${siteId}`,
        label: t('reports_register.column.site'),
        value: site ? siteLabel(site) : siteId,
        onRemove: () => toggleSite(siteId),
      };
    }),
  ];

  const rows = useMemo(
    () =>
      submissions.map((submission) => ({
        id: submission.id,
        registrar: submission.registrarName,
        region: submission.regionName || EMPTY_CELL,
        locality: submission.localityName || EMPTY_CELL,
        site: submission.siteName || EMPTY_CELL,
        confirmedAt: formatDateTime(submission.confirmedAtIso),
      })),
    [submissions],
  );

  const subtitleKey = isSystemAdministrator
    ? 'reports_register.subtitle.all'
    : 'reports_register.subtitle.own';

  // An empty register keeps its table. The wireframe leaves the card, the column
  // headers and the footer standing and puts the message in one centred cell
  // spanning every column (Rapoarte.tsx:107-135); the dashed box this replaces
  // took the whole register away, so a reader arriving at an empty period could
  // not even see what the register would hold. Both QueryState branches render
  // this same node: mud-table draws its `empty` slot on its own once `rows` is [].
  const register = (
    <>
      <div className="simsm-reports__table">
        <MudTable
          className="simsm-table"
          columns={columns}
          rows={rows}
          hoverable={true}
          rowStyle="zebra"
          aria-label={t('reports_register.table.aria_label')}
          onMudRowClick={onRowOpen<TableRowClickDetail>((_index, { row }) => {
            if (row.id) navigate(`${rolePrefix}/reports/${row.id}`);
          })}
        >
          {/* Every header is free to run to two lines. The Russian labels are
                the long ones — "Идентификатор отчёта", "Населённый пункт" —
                and left on mud-table's own nowrap `.th` they, not the values,
                set the column minimums and pushed the last column outside the
                frame.
                This is the wireframe's `.tbl.compact th { white-space: normal }`,
                whose comment says it exists so the table fits with the sidebar
                expanded. */}
          {columns.map((column) => (
            <span
              key={`header-${column.key}`}
              slot={`header-cell-${column.key}`}
              className="simsm-table__th"
            >
              {column.label}
            </span>
          ))}
          <EmptyRegisterMessage>
            {search
              ? t('reports_register.empty.search', { q: search })
              : isFiltered
                ? t('reports_register.empty.filtered')
                : t('reports_register.empty.period')}
          </EmptyRegisterMessage>
          {submissions.map((submission, index) => [
            // The report used to be openable with the mouse alone: mud-table
            // gives its body rows no tabindex, no role and no focusable
            // descendant, and this cell was a plain span, so Tab skipped
            // every row and Enter did nothing (audit E6, WCAG SC 2.1.1).
            // The identifier is AGE's link on the report's route (the
            // wireframe's `link` in the same cell, Rapoarte.tsx:126), which
            // is reachable, announced and works with the row click intact.
            // What it prints is the report's number, whole, the way the
            // wireframe prints it — the cell used to hold eight characters of
            // a uuid and an ellipsis, which is not a thing a person can read
            // out or write down. The uuid stays the route and the `title`
            // (specs/031-report-number).
            <RouterLink
              key={`id-${submission.id}`}
              slot={`cell-id-${index}`}
              className="simsm-reports__cell-id"
              title={submission.id}
              to={`${rolePrefix}/reports/${submission.id}`}
              size="sm"
              underline="hover"
              aria-label={t('reports_register.row.open', {
                number: submission.reportNumber,
                registrar: submission.registrarName || submission.registrarIdno,
                date: formatDateTime(submission.confirmedAtIso),
              })}
            >
              {submission.reportNumber}
            </RouterLink>,
            // The two cells below are slotted for their width, not their
            // content: left to mud-table's `.cell-text` a Registrar name
            // is one unbreakable 293px line, which on its own pushed the
            // last column outside the frame. The name wraps the way the
            // wireframe wraps it (Rapoarte.tsx, `.tbl.compact td`) while the
            // date stays whole.
            <span
              key={`registrar-${submission.id}`}
              slot={`cell-registrar-${index}`}
              className="simsm-table__wrap simsm-reports__cell-registrar"
            >
              {submission.registrarName}
            </span>,
            <span
              key={`confirmed-${submission.id}`}
              slot={`cell-confirmedAt-${index}`}
              className="simsm-table__keep"
            >
              {formatDateTime(submission.confirmedAtIso)}
            </span>,
          ])}
        </MudTable>
      </div>
      <TableFooter
        total={totalCount}
        page={page}
        pageSize={pageSize}
        onPage={(target) => {
          const next = new URLSearchParams(searchParams);
          next.set('p', String(target));
          setSearchParams(next);
        }}
        onPageSize={setPageSize}
      />
    </>
  );

  return (
    <section className="simsm-reports">
      <header className="simsm-reports__header">
        <div>
          <h1 className="simsm-reports__title">{t('reports_register.title')}</h1>
          {query.data ? (
            // UC93-01: the register says how many confirmed reports the current
            // scope and filters hold (BUG-1-035).
            <p className="simsm-reports__sub">
              {t('reports_register.count', { count: totalCount })}
            </p>
          ) : null}
          <p className="simsm-reports__sub">{t(subtitleKey, { count: totalCount })}</p>
        </div>
        {canFileReports ? (
          // AGE's button on an in-app route: `href` makes mud-button render its
          // own anchor, and only an unmodified primary click is routed, the way
          // RouterLink routes mud-link. It used to be a button inside a raw <a>:
          // two nested controls.
          <MudButton
            shape="circular"
            variant="primary"
            size="md"
            href={newReportHref}
            onClick={(event) => {
              if (
                event.button !== 0 ||
                event.metaKey ||
                event.altKey ||
                event.ctrlKey ||
                event.shiftKey
              ) {
                return;
              }
              event.preventDefault();
              navigate(newReportPath);
            }}
          >
            <MudIcon slot="icon-start" name="plus-large" size={20} />
            {t('reports_register.action.new')}
          </MudButton>
        ) : null}
      </header>

      <FilterBar
        // Row one, never a chip: the wireframe's `active` array holds the panel
        // facets alone (Rapoarte.tsx:68-75) because the typed text is already on
        // screen. "Resetează filtrele" clears it with everything else.
        search={{
          value: search,
          onChange: (value) => setParam('q', value),
          placeholder: t('reports_register.search.placeholder'),
          ariaLabel: t('reports_register.search.aria'),
          clearLabel: t('common.filter.clear_search'),
        }}
        active={activeFilters}
        onReset={() => setSearchParams(new URLSearchParams())}
        panelWidth={400}
        labels={{
          filters: t('common.filter.button'),
          reset: t('common.filter.reset'),
          panelAriaLabel: t('reports_register.filter.group'),
          activeAriaLabel: t('common.filter.active_aria_label'),
        }}
        panel={
          <>
            <label className="simsm-filter-bar__field">
              <span className="simsm-filter-bar__field-label">
                {t('reports_register.filter.period')}
              </span>
              <MudSelect
                size="medium"
                value={period.id}
                aria-label={t('reports_register.filter.period')}
                onMudChange={(e: CustomEvent<{ value: string }>) => onPeriodChange(e.detail.value)}
              >
                {periodOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </MudSelect>
            </label>

            {period.id === 'custom' ? (
              <div className="simsm-filter-bar__field-pair">
                <label className="simsm-filter-bar__field">
                  <span className="simsm-filter-bar__field-label">
                    {t('reports_register.filter.from')}
                  </span>
                  <MudDateInput
                    placeholder={t('common.date.placeholder')}
                    size="md"
                    aria-label={t('reports_register.filter.from')}
                    value={customDateValue('from')}
                    onMudInput={onCustomDateInput('from')}
                    onMudChange={onCustomDateChange('from')}
                  />
                </label>
                <label className="simsm-filter-bar__field">
                  <span className="simsm-filter-bar__field-label">
                    {t('reports_register.filter.to')}
                  </span>
                  <MudDateInput
                    placeholder={t('common.date.placeholder')}
                    size="md"
                    aria-label={t('reports_register.filter.to')}
                    value={customDateValue('to')}
                    onMudInput={onCustomDateInput('to')}
                    onMudChange={onCustomDateChange('to')}
                  />
                </label>
              </div>
            ) : null}

            {isSystemAdministrator ? (
              <label className="simsm-filter-bar__field">
                <span className="simsm-filter-bar__field-label">
                  {t('reports_register.filter.registrar')}
                </span>
                <MudSelect
                  size="medium"
                  value={applied.registrarIdno}
                  aria-label={t('reports_register.filter.registrar')}
                  onMudChange={(e: CustomEvent<{ value: string }>) =>
                    setParam('registrar', e.detail.value)
                  }
                >
                  {optionsWithAny(
                    registrarOptions,
                    t('reports_register.filter.all_registrars'),
                  ).map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </MudSelect>
              </label>
            ) : null}

            <label className="simsm-filter-bar__field">
              <span className="simsm-filter-bar__field-label">
                {t('reports_register.filter.region')}
              </span>
              <MudSelect
                size="medium"
                value={applied.regionId}
                aria-label={t('reports_register.filter.region')}
                onMudChange={(e: CustomEvent<{ value: string }>) => onRegionChange(e.detail.value)}
              >
                {optionsWithAny(filters.regions, t('reports_register.filter.all_regions')).map(
                  (option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ),
                )}
              </MudSelect>
            </label>

            <label className="simsm-filter-bar__field">
              <span className="simsm-filter-bar__field-label">
                {t('reports_register.filter.locality')}
              </span>
              <MudSelect
                size="medium"
                value={applied.localityId}
                aria-label={t('reports_register.filter.locality')}
                disabled={filters.localities.length === 0}
                onMudChange={(e: CustomEvent<{ value: string }>) =>
                  setParam('locality', e.detail.value)
                }
              >
                {optionsWithAny(
                  filters.localities,
                  t('reports_register.filter.all_localities'),
                ).map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </MudSelect>
            </label>

            {filters.sites.length > 0 ? (
              <div className="simsm-filter-bar__field">
                <span className="simsm-filter-bar__field-label">
                  {t('reports_register.filter.sites')}
                </span>
                {/* Sites are the one many-valued facet: selectable chips, not a
                    select, because a report register is read across several
                    storage locations at once. */}
                <div
                  className="simsm-reports__site-chips"
                  role="group"
                  aria-label={t('reports_register.filter.sites')}
                >
                  {filters.sites.map((site) => (
                    <MudChip
                      key={site.id}
                      selected={applied.siteIds.includes(site.id)}
                      onMudSelect={() => toggleSite(site.id)}
                    >
                      {siteLabel(site)}
                    </MudChip>
                  ))}
                </div>
              </div>
            ) : null}
          </>
        }
      />

      {/* A failed fetch is not an empty register: the branch used to run
          loading → empty → table with no `isError`, so a 500 read as "no reports
          in this period" (audit A2). The shared gate answers all three. */}
      <QueryState
        query={query}
        isEmpty={submissions.length === 0}
        loadingText={t('reports_register.loading')}
        empty={register}
      >
        {register}
      </QueryState>
    </section>
  );
}
